
import { IdefaultExpandedRowContext } from "@/components/widgets/tradeActionSummary/ActionSummaryTableHelperFunctions";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";

import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { Dispatch, RefObject, SetStateAction } from "react";
import { toast } from "react-toastify";
import { clearAllFiltersContextMenuObject } from "../contextMenusRightClick/genericContextMenuItems";

export const globalAGGridTableHeight: number = 22;

// Helper function to get row height based on user preference
export function getRowHeight(
  rowHeightSetting: "narrow" | "standard" | "expanded",
): number {
  const rowHeightMap = {
    narrow: 22,
    standard: 26,
    expanded: 32,
  };
  return rowHeightMap[rowHeightSetting] ?? rowHeightMap.narrow;
}

// Helper function to get font size based on user preference
export function getFontSizeClass(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): string {
  const fontSizeMap = {
    small: "text-xs",
    medium: "text-sm",
    large: "text-base",
    "x-large": "text-lg",
  };
  return fontSizeMap[fontSizeSetting] ?? fontSizeMap.small;
}

// Helper function to get AG Grid font size CSS variable
export function getFontSizePx(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): number {
  const fontSizeMap = {
    small: 12,
    medium: 13,
    large: 15,
    "x-large": 17,
  };
  return fontSizeMap[fontSizeSetting] ?? fontSizeMap.small;
}

// Header height = row height + 2px, based on the row height (spaciousness) setting.
export function getHeaderHeight(
  rowHeightSetting: "narrow" | "standard" | "expanded",
): number {
  return getRowHeight(rowHeightSetting) + 2;
}

// Helper function to get column width multiplier based on font size
export function getColumnWidthMultiplier(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): number {
  const multiplierMap = {
    small: 0.9,
    medium: 1.0,
    large: 1.2,
    "x-large": 1.6,
  };
  return multiplierMap[fontSizeSetting] ?? multiplierMap.small;
}

// Helper function to get internal cell element height based on row height setting
// Used for MUI Select containers inside AG Grid cells
export function getCellElementHeight(
  rowHeightSetting: "narrow" | "standard" | "expanded",
): number {
  const heightMap = {
    narrow: 18,
    standard: 22,
    expanded: 28,
  };
  return heightMap[rowHeightSetting] ?? heightMap.narrow;
}

// Helper function to get font size rem string for MUI MenuProps based on font size setting
export function getCellFontSizeRem(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): string {
  const sizeMap = {
    small: "0.65rem",
    medium: "0.7rem",
    large: "0.8rem",
    "x-large": "0.9rem",
  };
  return sizeMap[fontSizeSetting] ?? sizeMap.small;
}

// Helper function to get widget header font class based on font size setting
export function getWidgetHeaderFontClass(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): string {
  const classMap = {
    small: "text-sm",
    medium: "text-md",
    large: "text-base",
    "x-large": "text-lg",
  };
  return classMap[fontSizeSetting] ?? classMap.small;
}

export function getWidgetHeaderIconSize(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): string {
  const sizeMap = {
    small: "h-5 w-5",
    medium: "h-6 w-6",
    large: "h-6 w-6",
    "x-large": "h-6 w-6",
  };
  return sizeMap[fontSizeSetting] ?? sizeMap.small;
}

export function getWidgetHeaderSizeClass(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): string {
  const sizeMap: Record<string, string> = {
    small: "",
    medium: "h-7 py-1",
    large: "h-8 py-1",
    "x-large": "h-8 py-2",
  };
  return sizeMap[fontSizeSetting] ?? sizeMap.small;
}

// Helper function to get button size based on font size
export type ButtonSizeKey =
  | "default"
  | "icon_sm"
  | "icon_lg"
  | "icon_tooltip"
  | "button_sm"
  | "button_sm_medium"
  | "button_sm_large"
  | "button_sm_mainBlueAction"
  | "button_md"
  | "button_md_CP"
  | "button_lg";

export function getButtonSize(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
  baseSize: "sm" | "md" | "lg" = "sm",
): ButtonSizeKey {
  const sizeMap: Record<string, Record<string, ButtonSizeKey>> = {
    sm: {
      small: "button_sm",
      medium: "button_sm_medium",
      large: "button_sm_large",
      "x-large": "button_lg",
    },
    md: {
      small: "button_sm",
      medium: "button_md",
      large: "button_lg",
      "x-large": "button_lg",
    },
    lg: {
      small: "button_md",
      medium: "button_lg",
      large: "button_lg",
      "x-large": "button_lg",
    },
  };

  return sizeMap[baseSize]?.[fontSizeSetting] ?? sizeMap.sm.small;
}

// Helper function to get main blue action button size based on font size
export function getMainBlueActionButtonSize(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): "button_sm_mainBlueAction" | "button_md" | "button_lg" {
  const sizeMap = {
    small: "button_sm_mainBlueAction" as const,
    medium: "button_sm_mainBlueAction" as const,
    large: "button_lg" as const,
    "x-large": "button_lg" as const,
  };

  return sizeMap[fontSizeSetting] ?? sizeMap.small;
}

// Single source of truth for the height of every control inside the Trade
// Settings slide-in panels (OP + RP): the Advise / Auto-Affirm dropdowns
// (SettingDropdown), the RP Request / Released / Rejected buttons, and the OP
// Accept / Decline buttons. Using one constant guarantees they all line up
// exactly — borders/padding aside — at every font setting, and keeps them fixed
// (not "novelty large") at the largest font (LQC-1830).
export const TRADE_SETTINGS_CONTROL_HEIGHT = "h-[28px]";

// The height (Tailwind class) baked into each Button `size` variant in
// Button.tsx. This is the one bit not already expressed elsewhere: it lets a
// caller borrow just a variant's height without its width/text. Keep in sync
// with Button.tsx if a variant's h-* changes.
const BUTTON_SIZE_HEIGHT: Partial<Record<ButtonSizeKey, string>> = {
  button_sm: "h-4",
  button_sm_medium: "h-5",
  button_sm_large: "h-6",
  button_lg: "h-6",
};

// Height (Tailwind class) matching the Select Panel trade-creation buttons,
// which size via getButtonSize(fontSize, "sm"). Reuses that SAME fontSize→size
// mapping and just extracts the height, so a button can follow the identical
// height progression without adopting the variant's width/text (capped at h-6,
// so it never goes "novelty large" at x-large). Used by the OP slide-in's
// Update / Clear buttons (LQC-1830).
export function getSelectPanelButtonHeightClass(
  fontSizeSetting: "small" | "medium" | "large" | "x-large",
): string {
  return BUTTON_SIZE_HEIGHT[getButtonSize(fontSizeSetting, "sm")] ?? "h-4";
}

export function expandRowsFromSelectPanelAciton(
  gridRef: React.RefObject<AgGridReact<any>>,
) {
  if (gridRef) {
    let nodesWhereCleared = false;
    const nodesToExpand = tradeDataStore.getState().esidsToExpand;

    if (gridRef.current && gridRef.current.api && nodesToExpand.length > 0) {
      nodesToExpand.forEach((element) => {
        const node = gridRef.current?.api.getRowNode(element);
        if (node != undefined) {
          node.setExpanded(true);
          setTimeout(() => {
            tradeDataStore.getState().resetEsidsToExpand(); // Sets the array back to empty.
          }, 100);
          nodesWhereCleared = true;
        }
      });
    }
    return nodesWhereCleared;
  }
}

// ADMIN - See Action Workflow ES Status for buttons

// Buttons

export type CubeCustomCellRenererProps = Pick<
  CustomCellRendererProps,
  "data" | "node" | "api"
>;

export const defaultColDefActionSummary = {
  enableCellChangeFlash: false, // Turned off individual cell flashing as the whole row will always flash if an update is made.
  suppressHeaderMenuButton: true,
  filter: true,
  filterParams: {
    buttons: ["reset"],
  },
  cellStyle: { borderRight: "Var(--ag-grid-cubeCustom-cell-border)" },
  mainMenuItems: [
    "sortAscending",
    "sortDescending",
    "sortUnSort",
    "separator",
    "pinSubMenu",
    "separator",
    "autoSizeThis",
    "separator",
    "rowGroup",
    // "rowUnGroup", - can't access this button.
    "separator",
    "resetColumns",
    { ...clearAllFiltersContextMenuObject },
  ],
};

export const defaultColDef = {
  enableCellChangeFlash: true,
  suppressHeaderMenuButton: true,
  sortable: false,
  initialHide: true,
  cellStyle: { borderRight: "Var(--ag-grid-cubeCustom-cell-border)" },
  mainMenuItems: [
    "pinSubMenu",
    "separator",
    "autoSizeThis",
    "separator",
    "separator",
    "resetColumns",
  ],
};

export function toastWithMessage(message?: string) {
  message = message || "👷 Feature in Development 👷";

  toast.warn(message, {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    // theme: "colored",
    // transition: Bounce,
  });
}

//--------------------------------------------------------------------------------------------------------

// AG Grid Table size for drop down on trades:
export function generateGridHeightExpanded(
  executionRequestCounterParties?: any,
  rowHeight: number = 25,
  counterPartyRow: number = 35,
  headerHeight: number = 25,
  outOfGridHeight: number = 70,
): number {
  let rowCountCounterParty = 1; // Number of rows in your grid data
  if (executionRequestCounterParties) {
    rowCountCounterParty = executionRequestCounterParties.length;
  }

  let rowCountLegs = 1; // Number of rows in your grid data
  if (
    executionRequestCounterParties &&
    executionRequestCounterParties.length > 0
  ) {
    // need to check length of  executionRequestCounterParties in case trade status is at new and no CP's are selected.
    if (executionRequestCounterParties[0]?.legs) {
      rowCountLegs = executionRequestCounterParties[0].legs.length;
    }
  }

  const calculatedHeight =
    rowHeight * rowCountLegs * rowCountCounterParty +
    counterPartyRow * rowCountCounterParty +
    headerHeight * rowCountCounterParty +
    outOfGridHeight;

  return calculatedHeight;
}

export function generateGridHeight(
  gridRef: RefObject<AgGridReact>,
  data: any,
  isStructure: boolean = true,
  isActionSummaryTable: boolean = false,
  // counterPartyRow: number = 35,
  // headerHeight: number = 35,
  // outOfGridHeight: number = 55,
): number {
  const headerHeight: number = 35;
  const rowHeight = globalAGGridTableHeight; // globally defined.
  const isOrder = data.type == TradeType.Order;

  if (data.role == "RP") {
    const rowCountCounterParty = data.legs.length;
    const totalHeightWithoutExpandedRowsForBlocks =
      headerHeight + rowHeight * rowCountCounterParty + 10;

    if (!isStructure) {
      return totalHeightWithoutExpandedRowsForBlocks; // For outrights - needs to leave room for scroll bar at the bottom.
    } else {
      const heightForRPLegs = rowCountCounterParty * rowHeight;
      return totalHeightWithoutExpandedRowsForBlocks + heightForRPLegs;
    }
  } else {
    // Generating the height for the OP
    const rowCountCounterParty = data.executionRequestCounterParties?.length || 0; // Number of rows in your grid data (at the ES level)

    const totalHeightWithoutExpandedRowsForBlocks =
      headerHeight + rowHeight * rowCountCounterParty + 10;

    // if the trade is an outright
    if (!isStructure && !isOrder) {
      // ORDERS must be able to have a dropdown - the expanding table logic needs to run if the expand button on an order is clicked.
      return totalHeightWithoutExpandedRowsForBlocks; // For outrights - needs to leave room for scroll bar at the bottom.
    } else {
      // Need to calculate the number of nodes that are set to expanded.
      let expandedCount = 0; // The number of rows that have been set to expanded by the user.

      if (!isActionSummaryTable) {
        // If the action summary table is calculating it's requierd renderer height - do not count expanded nodes.
        if (gridRef && gridRef.current && gridRef.current.api) {
          gridRef.current.api.forEachNode((node) => {
            if (node.expanded) {
              expandedCount++;
            }
          });
        }
      }

      const numberOfBlocks =
        data.executionRequestCounterParties?.length > 0 &&
        data.executionRequestCounterParties[0]?.legs
          ? data.executionRequestCounterParties[0].legs.length
          : 0;
      const heightForBlockTable = numberOfBlocks * rowHeight;

      const heightForDetailTable = heightForBlockTable + headerHeight; // rows in detail table * global row height
      const heightForExpandedNodes = expandedCount * heightForDetailTable + 5; // rows x amount of nodes expanded.

      return totalHeightWithoutExpandedRowsForBlocks + heightForExpandedNodes;
    }
  }
}

// used for table heights within the table cells. This will be used for the ES and LEG tables:
export function generateGridHeightExpandedDetailRenderer(numOfRows: number) {
  const rowHeight = globalAGGridTableHeight; // Height of each row in pixels
  const headerHeight = 45; // Height of the header row in pixels - Also allowing enough hight for a scroll bar at the bottom of the tables.
  const rowCount = numOfRows; // Number of rows in your grid data

  return rowHeight * rowCount + headerHeight;
}

// OP Specific
export function expandAllNodes(
  gridRef: RefObject<AgGridReact>,
  setLegsAreExpanded?: React.Dispatch<React.SetStateAction<boolean>>,
) {
  if (gridRef) {
    gridRef.current?.api.forEachNode((node) => {
      node.setExpanded(true);
    });
  }
  if (setLegsAreExpanded) {
    setLegsAreExpanded(true);
  }
}
export function collapseAllNodes(
  gridRef: RefObject<AgGridReact>,
  setLegsAreExpanded?: React.Dispatch<React.SetStateAction<boolean>>,
) {
  if (gridRef) {
    gridRef.current?.api.forEachNode((node) => {
      node.setExpanded(false);
    });
  }
  if (setLegsAreExpanded) {
    setLegsAreExpanded(false);
  }
}

// CSS and table Styling: //
// BLUE for OP

// Yellow for Legs - can be used for both the OP and RP.
// LEGS NOT GETTING USED - Maybe bring back in CP Overrides.
// export function GenerateStyleLegCustomStyles(gridHeight: number, theme: any) {
//   // const { theme, setTheme } = useTheme();
//   if (theme == "light") {
//     return {
//       "--ag-background-color": cubeCustomColors.OPTableRowColour[600],
//       "--ag-odd-row-background-color":
//         cubeCustomColors.OPTableRowColour[
//           useTableUIStore.getState().tableStripeTradeBlotterOn ? 500 : 600
//         ],
//       "--ag-row-hover-color": cubeCustomColors.OPTableRowColour[400],
//       height: `${gridHeight}px`,
//     };
//   } else {
//     return {
//       "--ag-background-color": cubeCustomColors.OPTableRowColour[900],
//       "--ag-odd-row-background-color":
//         cubeCustomColors.OPTableRowColour[
//           useTableUIStore.getState().tableStripeTradeBlotterOn ? 800 : 900
//         ],
//       "--ag-row-hover-color": cubeCustomColors.OPTableRowColour[700],
//       height: `${gridHeight}px`,
//     };
//   }
// }

// -- END -- CSS and table Styling  -- END --  //

// -- Start: Functions to pull column data from tables -- //
// NOTE - functions will be used in the auto saving of data onto the widgets. These functions will use the table API to get relevant data for 'Auto save' capacity.
export function debounce<T extends (..._args: any[]) => void>(
  func: T,
  wait: number,
): (..._args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
// -- OND: Functions to pull column data from tables -- //

export function GenerateActionSummaryCustomStyles(
  theme: any,
  tableStripeOddTrue: boolean,
) {
  // Get colors from store
  const tradeBlotterColorLight =
    useTableUIStore.getState().tradeBlotterColorLight;
  const tradeBlotterColorDark =
    useTableUIStore.getState().tradeBlotterColorDark;
  const tradeBlotterFlashColorLight =
    useTableUIStore.getState().tradeBlotterFlashColorLight;
  const tradeBlotterFlashColorDark =
    useTableUIStore.getState().tradeBlotterFlashColorDark;

  // Helper function to adjust color brightness (for stripe variant)
  const adjustBrightness = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  // Used in the drop down of the Action Summary
  if (theme == "light") {
    const baseColor = tradeBlotterColorLight;
    const stripeColor = tableStripeOddTrue
      ? adjustBrightness(baseColor, -5)
      : baseColor;
    const hoverColor = adjustBrightness(baseColor, -10);

    return {
      "--ag-background-color": baseColor,
      "--ag-odd-row-background-color": stripeColor,
      "--ag-row-hover-color": hoverColor,
      "--ag-value-change-value-highlight-background-color":
        tradeBlotterFlashColorLight,
    };
  } else {
    const baseColor = tradeBlotterColorDark;
    const stripeColor = tableStripeOddTrue
      ? adjustBrightness(baseColor, 5)
      : baseColor;
    const hoverColor = adjustBrightness(baseColor, 10);

    return {
      "--ag-background-color": baseColor,
      "--ag-odd-row-background-color": stripeColor,
      "--ag-row-hover-color": hoverColor,
      "--ag-value-change-value-highlight-background-color":
        tradeBlotterFlashColorDark,
    };
  }
}

// Change all options to false when a node collapses.
export function onNodeCollapseChangeAllSettingsToFalse(
  expandedRowsContext: IdefaultExpandedRowContext[],
  setExpandedRowsContext: Dispatch<
    SetStateAction<IdefaultExpandedRowContext[]>
  >,
  esid: string,
  setExpandedRowsContextMostRecentlyUpdated?: Function,
) {
  const currentExpandedRowsContext = [...expandedRowsContext];
  const adjustedCurrentSettingsArray = currentExpandedRowsContext.map(
    // Adjust current settings.
    (settingsObj) =>
      settingsObj.esid == esid
        ? {
            ...settingsObj,
            allocationsShowing: false,
            editPanelShowing: false,
            legsShowing: false,
          }
        : settingsObj,
  );
  setExpandedRowsContext(adjustedCurrentSettingsArray);
  if (setExpandedRowsContextMostRecentlyUpdated) {
    setExpandedRowsContextMostRecentlyUpdated(esid);
  }
}

// Get the middle of the ESID for sorting on AG Grid.
export const formatIDToOnlyNumbers = (id: string) => {
  if (!id) {
    return 0;
  }
  const numbersToReturn: string[] = [];
  const idAsArray = [...id];

  const acceptedValues = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
  idAsArray.forEach((char) => {
    if (acceptedValues.includes(char)) {
      numbersToReturn.push(char);
    }
  });
  if (numbersToReturn.length === 0) {
    return 0;
  }
  const returnValueAsString = numbersToReturn.join("");
  const returnValue = Number(returnValueAsString);
  return returnValue;
};

/**
 * Helper function to apply column width multiplier to all columns in a column definition array
 * Columns can be flagged with:
 * - `fixedWidth: true` to prevent any scaling (for control columns like checkboxes, expand arrows)
 * - `extraScaling: true` to receive additional width at larger font sizes (3x the scale factor)
 * - `extraLargeScaling: true` to receive even more width at larger font sizes (5x the scale factor)
 * - `reducedScaling: true` to receive less width expansion at larger font sizes (50% of the scale factor)
 * @param colDefs - Array of column definitions
 * @param multiplier - The multiplier to apply to column widths (default 1.0)
 * @returns Array of column definitions with scaled widths
 */
export function applyColumnWidthMultiplier(
  colDefs: any[],
  multiplier: number = 1.0,
): any[] {
  return colDefs.map((colDef) => {
    const scaledColDef = { ...colDef };

    // Columns flagged with fixedWidth should never be scaled
    // Keep fixedWidth on the colDef so the runtime useEffect can also skip them
    if (colDef.fixedWidth) {
      delete scaledColDef.extraLargeScaling;
      delete scaledColDef.extraScaling;
      delete scaledColDef.reducedScaling;
      return scaledColDef;
    }

    // Determine the actual multiplier to use for this column
    let effectiveMultiplier = multiplier;

    if (multiplier > 1.0) {
      if (colDef.extraLargeScaling) {
        // Columns flagged with extraLargeScaling get 3.5x more scaling
        effectiveMultiplier = 0.6 + (multiplier - 1.0) * 3.5;
      } else if (colDef.extraScaling) {
        // Columns flagged with extraScaling get 3x more scaling
        effectiveMultiplier = 0.6 + (multiplier - 1.0) * 3;
      } else if (colDef.reducedScaling) {
        // Columns flagged with reducedScaling get 50% less scaling
        effectiveMultiplier = 1.0 + (multiplier - 1.0) * 0.5;
      }
    }

    if (scaledColDef.width && typeof scaledColDef.width === "number") {
      scaledColDef.width = Math.round(scaledColDef.width * effectiveMultiplier);
    }
    if (scaledColDef.minWidth && typeof scaledColDef.minWidth === "number") {
      scaledColDef.minWidth = Math.round(
        scaledColDef.minWidth * effectiveMultiplier,
      );
    }
    if (scaledColDef.maxWidth && typeof scaledColDef.maxWidth === "number") {
      scaledColDef.maxWidth = Math.round(
        scaledColDef.maxWidth * effectiveMultiplier,
      );
    }

    // Remove the scaling flags from the final column definition
    // to avoid passing unknown properties to AG Grid
    delete scaledColDef.extraLargeScaling;
    delete scaledColDef.extraScaling;
    delete scaledColDef.reducedScaling;

    return scaledColDef;
  });
}

// Ensure every column has a real colDef minWidth so AG Grid enforces a resize
// floor. The CSS header min-width (.text-* .ag-header-cell) is cosmetic only —
// it stops the header from visually collapsing but does NOT limit how far AG
// Grid resizes a column, so columns without a colDef minWidth could be dragged
// /scaled into their neighbours (header bleed). Explicit minWidths are left
// untouched (e.g. the Trade-wide settings column at 20).
export function applyDefaultMinWidths(colDefs: any[]): any[] {
  return colDefs.map((colDef) => {
    if (colDef.minWidth != null) return colDef; // explicit minWidth wins
    const next = { ...colDef };
    if (colDef.fixedWidth) {
      next.minWidth = colDef.width; // lock control cols (checkbox/expand arrow)
    } else if (typeof colDef.width === "number") {
      next.minWidth = Math.max(75, Math.round(colDef.width * 0.65));
    }
    return next;
  });
}
